import React, { useEffect, useId, useState } from 'react';
import type { SyncBar, Tone, UsagePoint } from '../../data/analytics-mock';

export const SYNC_SERIES: Array<{ key: keyof Pick<SyncBar, 'manual' | 'auto' | 'login' | 'other'>; label: string; tone: Tone }> = [
  { key: 'manual', label: 'Manual', tone: 'blue' },
  { key: 'auto', label: 'Auto', tone: 'green' },
  { key: 'login', label: 'Login', tone: 'purple' },
  { key: 'other', label: 'Other', tone: 'slate' },
];

function useNarrowChart(): boolean {
  const query = '(max-width: 720px)';
  const [narrow, setNarrow] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia(query);
    const onChange = () => setNarrow(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return narrow;
}

function smoothLine(points: Array<{ x: number; y: number }>, minY: number, maxY: number): string {
  if (points.length === 0) return '';
  const clamp = (value: number) => Math.min(maxY, Math.max(minY, value));
  const n = (value: number) => value.toFixed(1);
  let path = `M ${n(points[0].x)} ${n(points[0].y)}`;
  for (let index = 0; index < points.length - 1; index += 1) {
    const p0 = points[Math.max(0, index - 1)];
    const p1 = points[index];
    const p2 = points[index + 1];
    const p3 = points[Math.min(points.length - 1, index + 2)];
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = clamp(p1.y + (p2.y - p0.y) / 6);
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = clamp(p2.y - (p3.y - p1.y) / 6);
    path += ` C ${n(c1x)} ${n(c1y)}, ${n(c2x)} ${n(c2y)}, ${n(p2.x)} ${n(p2.y)}`;
  }
  return path;
}

export function Sparkline({ values, tone }: { values: number[]; tone: Tone }) {
  if (values.length < 2) return null;
  const width = 92;
  const height = 36;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const points = values.map((value, index) => ({
    x: (index / (values.length - 1)) * (width - 4) + 2,
    y: height - 4 - ((value - min) / span) * (height - 8),
  }));
  const line = smoothLine(points, 2, height - 2);
  const area = `${line} L ${points[points.length - 1].x.toFixed(1)} ${height} L ${points[0].x.toFixed(1)} ${height} Z`;
  return (
    <svg className={`an-spark an-tone an-tone--${tone}`} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <path className="an-spark__area" d={area} />
      <path className="an-spark__line" d={line} />
    </svg>
  );
}

function axisTicks(max: number): number[] {
  return [max, Math.round(max * 0.75), Math.round(max * 0.5), Math.round(max * 0.25), 0];
}

function splitLabel(label: string): { top: string; rest: string } {
  const parts = label.split(' ');
  return { top: parts[0] || label, rest: parts.slice(1).join(' ') };
}

export function UsageChart({ points, axisKb }: { points: UsagePoint[]; axisKb: number }) {
  const narrow = useNarrowChart();
  const rawId = useId().replace(/:/g, '');
  const wifiId = `an-wifi-${rawId}`;
  const mobileId = `an-mobile-${rawId}`;
  const [active, setActive] = useState<number | null>(null);

  if (points.length === 0) {
    return <p className="an-note">No network samples in this range.</p>;
  }

  const asMb = axisKb >= 1500;
  const axis = asMb ? Math.max(1, Math.ceil(axisKb / 1024)) : Math.max(axisKb, 1);
  const scale = asMb ? 1 / 1024 : 1;
  const width = 720;
  const height = 268;
  const pad = { l: 58, r: 12, t: 12, b: 42 };
  const plotW = width - pad.l - pad.r;
  const plotH = height - pad.t - pad.b;
  const baseline = pad.t + plotH;
  const xAt = (index: number) =>
    pad.l + (points.length === 1 ? plotW / 2 : (index / (points.length - 1)) * plotW);
  const yAt = (kb: number) => baseline - (Math.max(0, Math.min(axis, kb * scale)) / axis) * plotH;
  const wifi = points.map((point, index) => ({ x: xAt(index), y: yAt(point.wifiKb) }));
  const mobile = points.map((point, index) => ({ x: xAt(index), y: yAt(point.mobileKb) }));
  const wifiLine = smoothLine(wifi, pad.t, baseline);
  const mobileLine = smoothLine(mobile, pad.t, baseline);
  const close = (pts: Array<{ x: number; y: number }>, line: string) =>
    `${line} L ${pts[pts.length - 1].x.toFixed(1)} ${baseline.toFixed(1)} L ${pts[0].x.toFixed(1)} ${baseline.toFixed(1)} Z`;
  const ticks = axisTicks(axis);
  const compact = narrow || points.length > 7;
  const amount = (kb: number) => (asMb ? `${(kb / 1024).toFixed(2)} MB` : `${Math.round(kb)} KB`);

  return (
    <div className="an-plot">
      <table className="visually-hidden">
        <caption>Network usage over time</caption>
        <thead>
          <tr>
            <th scope="col">When</th>
            <th scope="col">Mobile data</th>
            <th scope="col">Wi-Fi and other</th>
          </tr>
        </thead>
        <tbody>
          {points.map((point, index) => (
            <tr key={`${point.label}-${index}`}>
              <th scope="row">{point.label}</th>
              <td>{amount(point.mobileKb)}</td>
              <td>{amount(point.wifiKb)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="presentation"
        aria-hidden="true"
        onMouseLeave={() => setActive(null)}
      >
        <defs>
          <linearGradient id={wifiId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--an-blue)" stopOpacity="0.38" />
            <stop offset="100%" stopColor="var(--an-blue)" stopOpacity="0.03" />
          </linearGradient>
          <linearGradient id={mobileId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--an-green)" stopOpacity="0.4" />
            <stop offset="100%" stopColor="var(--an-green)" stopOpacity="0.03" />
          </linearGradient>
        </defs>
        {ticks.map((tick) => {
          const y = pad.t + plotH - (tick / axis) * plotH;
          return (
            <g key={tick}>
              <line className="an-gridline" x1={pad.l} x2={width - pad.r} y1={y} y2={y} />
              <text className="an-axis" x={pad.l - 8} y={y + 4} textAnchor="end">
                {tick} {asMb ? 'MB' : 'KB'}
              </text>
            </g>
          );
        })}
        <path d={close(wifi, wifiLine)} fill={`url(#${wifiId})`} />
        <path d={close(mobile, mobileLine)} fill={`url(#${mobileId})`} />
        <path className="an-line an-line--blue" d={wifiLine} />
        <path className="an-line an-line--green" d={mobileLine} />
        {active != null && (
          <g>
            <line
              className="an-guide"
              x1={wifi[active].x}
              x2={wifi[active].x}
              y1={pad.t}
              y2={baseline}
            />
            <circle className="an-node an-node--blue" cx={wifi[active].x} cy={wifi[active].y} r="4.5" />
            <circle className="an-node an-node--green" cx={mobile[active].x} cy={mobile[active].y} r="4.5" />
          </g>
        )}
        {points.map((point, index) => {
          const lines = splitLabel(point.label);
          const x = xAt(index);
          return (
            <text key={`${point.label}-${index}`} className="an-axis" x={x} y={height - 24} textAnchor="middle">
              {compact ? (
                point.shortLabel
              ) : (
                <>
                  <tspan x={x}>{lines.top}</tspan>
                  <tspan x={x} dy="13">
                    {lines.rest}
                  </tspan>
                </>
              )}
            </text>
          );
        })}
        {points.map((point, index) => {
          const slot = plotW / points.length;
          return (
            <rect
              key={`${point.label}-hit-${index}`}
              x={xAt(index) - slot / 2}
              y={pad.t}
              width={slot}
              height={plotH}
              fill="transparent"
              onMouseEnter={() => setActive(index)}
              onClick={() => setActive(index)}
            />
          );
        })}
      </svg>
      {active != null && (
        <div
          className="an-float"
          style={{
            left: `${Math.min(84, Math.max(16, (xAt(active) / width) * 100))}%`,
            top: `${(Math.min(wifi[active].y, mobile[active].y) / height) * 100}%`,
          }}
        >
          <strong>{points[active].label}</strong>
          <span>
            <i className="an-dot an-tone an-tone--green" />
            Mobile {amount(points[active].mobileKb)}
          </span>
          <span>
            <i className="an-dot an-tone an-tone--blue" />
            Wi-Fi {amount(points[active].wifiKb)}
          </span>
        </div>
      )}
    </div>
  );
}

export interface DonutSlice {
  id: string;
  label: string;
  tone: Tone;
  value: number;
  display: string;
}

export function DonutChart({
  slices,
  centerValue,
  centerCaption,
}: {
  slices: DonutSlice[];
  centerValue: string;
  centerCaption: string;
}) {
  const size = 176;
  const center = 88;
  const radius = 64;
  const stroke = 16;
  const circumference = 2 * Math.PI * radius;
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  const gap = total > 0 && slices.length > 1 ? 2.4 : 0;
  let cursor = 0;
  const arcs = slices.map((slice) => {
    const portion = total > 0 ? (slice.value / total) * circumference : 0;
    const arc = { ...slice, length: Math.max(0, portion - gap), offset: cursor };
    cursor += portion;
    return arc;
  });

  return (
    <div className="an-donut-layout">
      <div className="an-donut">
        <svg viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
          <circle className="an-donut__track" cx={center} cy={center} r={radius} />
          <g transform={`rotate(-90 ${center} ${center})`}>
            {arcs.map((arc) => (
              <circle
                key={arc.id}
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={`var(--an-${arc.tone})`}
                strokeWidth={stroke}
                strokeDasharray={`${arc.length} ${circumference - arc.length}`}
                strokeDashoffset={-arc.offset}
              />
            ))}
          </g>
        </svg>
        <div className="an-donut__mid">
          <strong>{centerValue}</strong>
          <span>{centerCaption}</span>
        </div>
      </div>
      <ul className="an-slices">
        {slices.map((slice) => {
          const share = total > 0 ? (slice.value / total) * 100 : 0;
          return (
            <li key={slice.id}>
              <i className={`an-dot an-tone an-tone--${slice.tone}`} />
              <span className="an-slices__name">{slice.label}</span>
              <span className="an-slices__val">{slice.display}</span>
              <span className="an-slices__pct">{share.toFixed(1)}%</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function syncTotal(day: SyncBar): number {
  return day.manual + day.auto + day.login + day.other;
}

export function SyncChart({ days, axisMax }: { days: SyncBar[]; axisMax: number }) {
  const narrow = useNarrowChart();
  const [active, setActive] = useState<number | null>(null);
  if (days.length === 0) return <p className="an-note">No syncs in this range.</p>;

  const peak = Math.max(...days.map(syncTotal), 1);
  const axis = axisMax > 0 ? axisMax : peak;
  const ticks = axisTicks(axis);
  const visual = [...SYNC_SERIES].reverse();

  return (
    <div className="an-bars">
      <div className="an-bars__y" aria-hidden="true">
        {ticks.map((tick) => (
          <span key={tick}>{tick}</span>
        ))}
      </div>
      <div className="an-bars__main">
        <div className="an-bars__plot">
          <div className="an-bars__grid" aria-hidden="true">
            {ticks.map((tick) => (
              <span key={tick} />
            ))}
          </div>
          {days.map((day, index) => {
            const total = syncTotal(day);
            const lines = splitLabel(day.label);
            return (
              <button
                key={`${day.label}-${index}`}
                type="button"
                className={`an-bars__col${active === index ? ' is-on' : ''}`}
                aria-label={`${day.label}, ${total} syncs. Manual ${day.manual}, Auto ${day.auto}, Login ${day.login}, Other ${day.other}.`}
                onMouseEnter={() => setActive(index)}
                onMouseLeave={() => setActive(null)}
                onFocus={() => setActive(index)}
                onBlur={() => setActive(null)}
              >
                <span className="an-stack" style={{ height: `${Math.min(100, (total / axis) * 100)}%` }}>
                  {visual.map((series) =>
                    day[series.key] > 0 ? (
                      <i
                        key={series.key}
                        className={`an-stack__seg an-tone an-tone--${series.tone}`}
                        style={{ flexGrow: day[series.key] }}
                      />
                    ) : null
                  )}
                </span>
                <span className="visually-hidden">
                  {narrow ? day.shortLabel : `${lines.top} ${lines.rest}`}
                </span>
              </button>
            );
          })}
          {active != null && (
            <div
              className="an-float an-float--bar"
              style={{ left: `${((active + 0.5) / days.length) * 100}%` }}
            >
              <strong>{days[active].label}</strong>
              <span>{syncTotal(days[active])} syncs</span>
              {SYNC_SERIES.map((series) => (
                <span key={series.key}>
                  <i className={`an-dot an-tone an-tone--${series.tone}`} />
                  {series.label} {days[active][series.key]}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="an-bars__names" aria-hidden="true">
          {days.map((day, index) => {
            const lines = splitLabel(day.label);
            return (
              <span key={`${day.label}-name-${index}`}>
                {narrow ? (
                  day.shortLabel
                ) : (
                  <>
                    <b>{lines.top}</b>
                    {lines.rest}
                  </>
                )}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
